import { BarChart } from '@mui/icons-material';
import { IconButton, type SxProps, type Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { useModal } from '../../hooks/useDialog';
import useMatchWidgetConfig from '../../hooks/useMatchWidgetConfig';
import { mergeSx } from '../../utils/styles';
import PreMatchWidgetModal, {
  PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER,
} from '../widgets/pre-match-widget/PreMatchWidgetModal';
import type { PreMatchWidgetSeedEvent } from '../widgets/pre-match-widget/PreMatchWidgetModal';
import {
  COMPACT_WIDGET_VIEWPORT_QUERY,
  MOBILE_PORTRAIT_WIDGET_HEIGHT,
  MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY,
  PRE_MATCH_WIDGET_MAX_WIDTH,
  PRE_MATCH_WIDGET_MOBILE_LANDSCAPE_WIDTH,
  SHORT_DESKTOP_VIEWPORT_QUERY,
  TABLET_PORTRAIT_WIDGET_HEIGHT,
  TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY,
  TABLET_PORTRAIT_WIDGET_WIDTH,
  TALL_DESKTOP_VIEWPORT_QUERY,
  TALL_DESKTOP_WIDGET_MAX_WIDTH,
} from '../widgets/pre-match-widget/styles';

const PRE_MATCH_WIDGET_MODAL_PROPS = {
  anchor: 'top' as const,
  useSwipeableDrawer: true,
  maxWidth: false as const,
  slotProps: {
    paper: {
      sx: {
        width: PRE_MATCH_WIDGET_MAX_WIDTH,
        maxWidth: '100vw',
        minWidth: { xs: 'unset', md: 'unset' },
        maxHeight: { xs: '100dvh', md: '90dvh' },
        m: 0,
        mx: { xs: 'auto', md: 'auto' },
        top: { xs: 0, md: 'auto' },
        left: 0,
        right: 0,
        bottom: 'auto',
        overflow: 'hidden',
        boxSizing: 'border-box',
        borderRadius: 4,
        [SHORT_DESKTOP_VIEWPORT_QUERY]: {
          maxHeight: '100dvh',
        },
        [TALL_DESKTOP_VIEWPORT_QUERY]: {
          width: TALL_DESKTOP_WIDGET_MAX_WIDTH,
          maxWidth: `min(100vw, ${TALL_DESKTOP_WIDGET_MAX_WIDTH})`,
        },
        [COMPACT_WIDGET_VIEWPORT_QUERY]: {
          width: PRE_MATCH_WIDGET_MOBILE_LANDSCAPE_WIDTH,
          maxWidth: PRE_MATCH_WIDGET_MOBILE_LANDSCAPE_WIDTH,
        },
        [MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
          width: '100%',
          maxWidth: '100vw',
          height: `calc(${MOBILE_PORTRAIT_WIDGET_HEIGHT} + ${PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER})`,
          maxHeight: `calc(${MOBILE_PORTRAIT_WIDGET_HEIGHT} + ${PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER})`,
        },
        [TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
          width: TABLET_PORTRAIT_WIDGET_WIDTH,
          maxWidth: `min(100vw, ${TABLET_PORTRAIT_WIDGET_WIDTH})`,
          height: `calc(${TABLET_PORTRAIT_WIDGET_HEIGHT} + ${PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER})`,
          maxHeight: `calc(${TABLET_PORTRAIT_WIDGET_HEIGHT} + ${PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER})`,
        },
      },
    },
  },
};

type Size = 'small' | 'medium' | 'large';

const ICON_DIMENSION_BY_SIZE: Record<Size, { xs: number; md: number }> = {
  small: { xs: 14, md: 16 },
  medium: { xs: 16, md: 18 },
  large: { xs: 18, md: 20 },
};

const styles = {
  button: {
    '&.MuiIconButton-root': {
      '&:hover': { backgroundColor: 'transparent' },
      p: 0,
      minWidth: 0,
    },
  },
  icon: (size: Size) => ({
    color: 'neutral.100',
    width: ICON_DIMENSION_BY_SIZE[size],
    height: ICON_DIMENSION_BY_SIZE[size],
    fontSize: ICON_DIMENSION_BY_SIZE[size],
    '&:hover': { color: 'neutral.25' },
  }),
};

type UpcomingStatsButtonProps = {
  statsId?: string | null;
  sportId?: string;
  event?: PreMatchWidgetSeedEvent | null;
  size?: Size;
  sx?: SxProps<Theme>;
  iconSx?: SxProps<Theme>;
  fallback?: ReactNode;
};

const UpcomingStatsButton = ({
  statsId,
  sportId,
  event,
  size = 'medium',
  sx,
  iconSx,
  fallback = null,
}: UpcomingStatsButtonProps) => {
  const { groupConfigurationId, isWidgetAvailable } = useMatchWidgetConfig('preMatch', sportId ?? '');
  const { openModal } = useModal();

  if (!isWidgetAvailable || !statsId) return fallback;

  return (
    <IconButton
      aria-label='Open match statistics'
      data-testid='upcoming-stats-button'
      onClick={() => {
        openModal(
          <PreMatchWidgetModal
            statsId={statsId}
            sportId={sportId}
            configurationId={groupConfigurationId ?? ''}
            seedEvent={event}
          />,
          PRE_MATCH_WIDGET_MODAL_PROPS
        );
      }}
      sx={mergeSx(styles.button, sx)}
    >
      <BarChart sx={mergeSx(styles.icon(size), iconSx)} />
    </IconButton>
  );
};

export default UpcomingStatsButton;
